import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container"];

  static values = {
    customFieldConditions: { type: Array, default: [] }
  };

  connect() {
    this._initConditions();
    this._attachEventListeners();
    setTimeout(() => this._evaluateConditions(), 100);
  }

  _initConditions() {
    const attr = this.element.getAttribute('data-custom-field-conditions-value');
    
    if (attr && attr.length > 0 && attr !== '[]' && attr !== '') {
      try {
        // If it's already a valid JSON string, parse it
        if (attr.trim().startsWith('[')) {
          this.customFieldConditionsValue = JSON.parse(attr);
        }
      } catch (e) {
        console.error("Failed to parse conditions:", e, "Value:", attr);
      }
    }
  }

  _attachEventListeners() {
    this.element.addEventListener("change", () => this._evaluateConditions());
    this.element.addEventListener("input", () => this._evaluateConditions());
    
    // Listen for validation request
    this.element.addEventListener("validate-custom-fields", (e) => {
      const isValid = this.validate();
      this.element.dataset.validationValid = isValid;
      if (!isValid) {
        e.preventDefault();
      }
    });
  }

  _evaluateConditions() {
    const container = this.element;
    const conditionsByChild = {};

    this.customFieldConditionsValue.forEach((condition) => {
      if (!conditionsByChild[condition.child_field_id]) {
        conditionsByChild[condition.child_field_id] = [];
      }
      conditionsByChild[condition.child_field_id].push(condition);
    });

    Object.keys(conditionsByChild).forEach((childFieldId) => {
      const conditions = conditionsByChild[childFieldId];
      this._evaluateChildConditions(container, childFieldId, conditions);
    });
  }

  _evaluateChildConditions(container, childFieldId, conditions) {
    const childField = container.querySelector(`[data-custom-field-id="${childFieldId}"]`);
    if (!childField) return;

    const results = conditions.map((condition) => 
      this._evaluateSingleCondition(container, condition)
    );

    const orConditions = conditions.filter((c) => c.logic_operator === "OR");
    let shouldShow;

    if (orConditions.length > 0) {
      const orResults = orConditions.map((c) => 
        this._evaluateSingleCondition(container, c)
      );
      shouldShow = orResults.some((r) => r === true);

      const andConditions = conditions.filter((c) => c.logic_operator === "AND");
      if (andConditions.length > 0) {
        const andResults = andConditions.map((c) => 
          this._evaluateSingleCondition(container, c)
        );
        shouldShow = shouldShow && andResults.every((r) => r === true);
      }
    } else {
      shouldShow = results.every((r) => r === true);
    }

    this._setFieldVisibility(childField, shouldShow);
  }

  _evaluateSingleCondition(container, condition) {
    const parentField = container.querySelector(`[data-custom-field-id="${condition.parent_field_id}"]`);
    if (!parentField) return false;

    const input = this._getInputFromField(parentField);
    if (!input) return false;

    const actualValue = input.type === "checkbox" 
      ? (input.checked ? "true" : "false") 
      : input.value;
    const expectedValue = condition.expected_value;

    switch (condition.operator) {
      case "equals":
        return actualValue === expectedValue;
      case "not_equals":
        return actualValue !== expectedValue;
      case "contains":
        return actualValue && actualValue.toString().toLowerCase().includes(expectedValue.toLowerCase());
      case "greater_than":
        return !isNaN(actualValue) && !isNaN(expectedValue) && parseFloat(actualValue) > parseFloat(expectedValue);
      case "less_than":
        return !isNaN(actualValue) && !isNaN(expectedValue) && parseFloat(actualValue) < parseFloat(expectedValue);
      case "is_empty":
        return !actualValue || actualValue === "";
      case "is_not_empty":
        return !!actualValue && actualValue !== "";
      default:
        return false;
    }
  }

  _getInputFromField(fieldBlock) {
    return (
      fieldBlock.querySelector('input[type="radio"]:checked') ||
      fieldBlock.querySelector('input[type="checkbox"]') ||
      fieldBlock.querySelector("select") ||
      fieldBlock.querySelector('input[type="text"]') ||
      fieldBlock.querySelector('input[type="number"]') ||
      fieldBlock.querySelector('input[type="date"]') ||
      fieldBlock.querySelector('input[type="url"]')
    );
  }

  _setFieldVisibility(fieldBlock, visible) {
    const wrapper = fieldBlock.closest(".custom-field-wrapper");
    if (wrapper) {
      if (visible) {
        wrapper.classList.remove("d-none");
      } else {
        // Reset field value when hiding conditional field
        this._resetFieldValue(wrapper);
        wrapper.classList.add("d-none");
      }
    }
  }

  _resetFieldValue(wrapper) {
    const input = wrapper.querySelector('.custom-field-input');
    if (!input) return;

    if (input.type === 'radio') {
      const noRadio = wrapper.querySelector('input[value="no"]') || 
                      wrapper.querySelector('input[type="radio"]:not([value="yes"])');
      if (noRadio) {
        noRadio.checked = true;
      }
    } else if (input.type === 'checkbox') {
      input.checked = false;
    } else {
      input.value = '';
    }
  }

  validate() {
    const container = this.element;
    let isValid = true;
    
    // Clear previous error messages
    container.querySelectorAll('.custom-field-error').forEach(el => el.remove());
    
    // Find all visible required custom fields
    const wrappers = container.querySelectorAll('.custom-field-wrapper');
    wrappers.forEach(wrapper => {
      if (wrapper.classList.contains('d-none')) return;
      
      const isRequired = wrapper.dataset.fieldRequired === 'true';
      if (!isRequired) return;
      
      const hasConditions = wrapper.dataset.fieldHasConditions === 'true';
      
      // If field has conditions, only validate if parent field condition is met
      if (hasConditions) {
        const fieldId = wrapper.dataset.customFieldId;
        const conditions = this.customFieldConditionsValue.filter(c => c.child_field_id == fieldId);
        
        // Check if any condition is satisfied
        let conditionMet = false;
        for (const condition of conditions) {
          const parentField = container.querySelector(`[data-custom-field-id="${condition.parent_field_id}"]`);
          if (!parentField) continue;
          
          const parentInput = this._getInputFromField(parentField);
          if (!parentInput) continue;
          
          const actualValue = parentInput.type === "checkbox" 
            ? (parentInput.checked ? "true" : "false") 
            : parentInput.value;
          
          if (condition.operator === 'equals' && actualValue === condition.expected_value) {
            conditionMet = true;
            break;
          }
        }
        
        // If condition not met, skip validation for this required field
        if (!conditionMet) return;
      }
      
      const input = wrapper.querySelector('.custom-field-input');
      if (!input) return;
      
      let hasValue = false;
      if (input.type === 'radio') {
        const checked = wrapper.querySelector('input[type="radio"]:checked');
        hasValue = !!checked;
      } else if (input.type === 'checkbox') {
        hasValue = input.checked;
      } else {
        hasValue = !!input.value.trim();
      }
      
      if (!hasValue) {
        isValid = false;
        // Add error styling
        wrapper.classList.add('is-invalid');
        const errorDiv = document.createElement('div');
        errorDiv.className = 'custom-field-error text-danger small mt-1';
        errorDiv.textContent = 'This field is required';
        wrapper.appendChild(errorDiv);
      } else {
        wrapper.classList.remove('is-invalid');
      }
    });
    
    return isValid;
  }
}
